import {
  TItemStatCalculation,
  TItemStatsSummary,
  TLeadAttributionByItem,
  TLeadAttributionByJourneyStatsDto,
  TLeadAttributionByOfficeStatsDto,
  TLeadAttributionBySourceStatsDto,
  TLeadAttributionByUtmCampaignStatsDto,
  TLeadAttributionCalculationMatrix,
  TLeadAttributionStatItemByTypeByGroup,
  TStatAttributeCalculation,
} from "../types/account-lead-attribution-analyticis.type";
import {
  LeadAttributionGroupedEnum,
  LeadAttributionStatusEnum,
  LeadAttributionTypesEnum,
} from "../enums/account-lead-attribution.enum";
import { sortByKey } from "./object.utils";
import { groupToEventTypesMap } from "../constants/account-lead-attribution";

export function getLeadAttributionStatsForItem<
  T extends TLeadAttributionByItem
>(
  byItem: T,
  leadAttributionCalculationMatrix: TLeadAttributionCalculationMatrix
): TItemStatsSummary<T> {
  let totalEstValuePerItem = 0;
  const itemStats: TItemStatCalculation[] = byItem.stats.map(stat => {
    let totalCountByStat = 0;
    const statAttributeCalculations: TStatAttributeCalculation[] = [];
    const calculationInstructionsForStat =
      leadAttributionCalculationMatrix[stat.name];
    stat.attributes.forEach(attribute => {
      totalCountByStat += attribute.count;
      const calculationInstructionsForAttribute =
        calculationInstructionsForStat?.[attribute.name];
      if (
        !!calculationInstructionsForStat &&
        !!calculationInstructionsForAttribute
      ) {
        const monetaryValue =
          attribute.count * calculationInstructionsForAttribute.multiplier;
        statAttributeCalculations.push({
          label: calculationInstructionsForAttribute.label,
          count: attribute.count,
          monetaryValue,
        });
        if (stat.name === LeadAttributionGroupedEnum.COMPLETIONS) {
          totalEstValuePerItem += monetaryValue;
        }
      }
    });
    return {
      title: stat.name,
      totalCountByStat,
      statAttributeCalculations,
    };
  });

  const sortedItemStats = sortByKey(itemStats, "title", "desc");

  return {
    ...byItem,
    totalEstValuePerItem,
    preparedStatsPerItem: sortedItemStats,
  };
}

export function getLeadAttributionItemUnknownLabel(
  leadAttributionType: LeadAttributionTypesEnum
) {
  return `Unknown ${leadAttributionType}`;
}

export const transformLeadAttributionStatItem = (
  stat: TLeadAttributionStatItemByTypeByGroup | null
) => {
  if (!stat) return null;
  switch (stat.type) {
    case LeadAttributionTypesEnum.OFFICE:
      const item = stat.item as TLeadAttributionByOfficeStatsDto;
      return {
        name: item.officeName,
        type: stat.type,
        group: stat.group,
      };
    case LeadAttributionTypesEnum.JOURNEY:
      const journeyItem = stat.item as TLeadAttributionByJourneyStatsDto;
      return {
        name: journeyItem.journeyName,
        type: stat.type,
        group: stat.group,
      };
    case LeadAttributionTypesEnum.CAMPAIGN:
      const campaignItem = stat.item as TLeadAttributionByUtmCampaignStatsDto;
      return {
        name: campaignItem.utmCampaign,
        type: stat.type,
        group: stat.group,
      };
    case LeadAttributionTypesEnum.SOURCE:
      const sourceItem = stat.item as TLeadAttributionBySourceStatsDto;
      return {
        name: sourceItem.sourceName,
        type: stat.type,
        group: stat.group,
      };
    default:
      return null;
  }
};

export const getStatusesForGroup = (
  group: LeadAttributionGroupedEnum
): LeadAttributionStatusEnum[] => {
  return groupToEventTypesMap[group] ?? [];
};
